import {
  ConfirmModal,
  dateColumn,
  EMPTY_VALUE,
  Section,
  tableStyleOverrides,
  textColumn,
  MUI_X_5_GRID_PROPS,
  MoreActionsIcon,
  useGridApiRefKeepingPage,
} from '@dispatch/web/ui';
import { Stack, Typography } from '@mui/material';
import {
  DataGridPro,
  GridRenderCellParams,
  GridActionsCellItem,
  GridColDef,
  GridRowParams,
  GridSortCellParams,
  gridDateComparator,
} from '@mui/x-data-grid-pro';
import React from 'react';
import { ResourceContext } from '../../ResourcesDetails';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  CertificationAndLicense,
  SelectedCertificationsAndLicenses,
} from '@dispatch/shared/types';
import { isRunningInJest } from '@dispatch/testing';

export function CertsAndLicensesList({
  onEditRow,
  onDeleteLicOrCert,
  isLoading,
  certsAndLicense,
  isReadOnly,
}: {
  onEditRow: (row: SelectedCertificationsAndLicenses) => void;
  onDeleteLicOrCert: (licenseId: string) => void;
  isLoading: boolean;
  certsAndLicense: CertificationAndLicense[];
  isReadOnly?: boolean;
}) {
  const apiRef = useGridApiRefKeepingPage();
  const { data } = React.useContext(ResourceContext);
  const [toDelete, setToDelete] = React.useState<string | null>(null);

  const onDeleteLicense = () => {
    if (toDelete) {
      onDeleteLicOrCert(toDelete);
      setToDelete(null);
    }
  };

  const compareDates = (
    one: Date | string,
    another: Date | string,
    cellParams1: GridSortCellParams,
    cellParams2: GridSortCellParams
  ) => {
    if (one === EMPTY_VALUE && another === EMPTY_VALUE) {
      return 0;
    }
    if (one === EMPTY_VALUE) {
      return 1;
    }
    if (another === EMPTY_VALUE) {
      return -1;
    }
    return gridDateComparator(one, another, cellParams1, cellParams2);
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      valueGetter: (_value, row) => {
        const companyCert = certsAndLicense?.find(
          (cert) =>
            cert.integrationGUID === row?.certificationAndLicenseIntegrationGUID
        );
        return companyCert?.name;
      },
      renderCell: (params: GridRenderCellParams) => {
        const companyCert = data?.company?.certificationAndLicense.find(
          (cert) =>
            cert.integrationGUID ===
            params.row?.certificationAndLicenseIntegrationGUID
        );

        const name = companyCert?.name || EMPTY_VALUE;
        const verificationType = companyCert?.verificationType || EMPTY_VALUE;
        return (
          <div>
            <Typography>{name}</Typography>
            <Typography color="GrayText">{verificationType}</Typography>
          </div>
        );
      },
      flex: 1,
    },
    textColumn({
      field: 'licenseNumber',
      headerName: 'License #',
      flex: 1,
    }),
    dateColumn(
      {
        field: 'issueDate',
        headerName: 'Start Date',
        valueGetter: (_value, row) =>
          row?.issueDate ? new Date(row.issueDate) : EMPTY_VALUE,
        flex: 1,
        sortComparator: compareDates,
      },
      'MMM dd, yyyy'
    ),
    dateColumn(
      {
        field: 'expirationDate',
        headerName: 'Expiration Date',
        valueGetter: (_value, row) =>
          row.expirationDate ? new Date(row.expirationDate) : EMPTY_VALUE,
        flex: 1,
        type: 'date',
        sortComparator: compareDates,
      },
      'MMM dd, yyyy'
    ),
    {
      field: 'actions',
      type: 'actions',
      width: 80,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          key="edit"
          icon={<EditIcon />}
          label="EDIT"
          onClick={() => onEditRow(params.row)}
          showInMenu
          disabled={isReadOnly}
        />,
        <GridActionsCellItem
          key="deactivate"
          icon={<DeleteIcon />}
          label="DEACTIVATE"
          onClick={() => setToDelete(params.row.licenseNumber)}
          showInMenu
          disabled={isReadOnly}
        />,
      ],
    },
  ];

  return (
    <Section>
      <div style={{ height: '500px' }}>
        <DataGridPro
          apiRef={apiRef}
          {...MUI_X_5_GRID_PROPS}
          disableVirtualization={isRunningInJest()}
          loading={isLoading}
          rows={data?.selectedCertificationsAndLicenses || []}
          slots={{
            moreActionsIcon: MoreActionsIcon,
            noRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                No Assigned Certifications or Licenses
              </Stack>
            ),
            noResultsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                Filter Returned No Certification or Licenses
              </Stack>
            ),
          }}
          /**
           * mui GridColDef is not registering that getActions is part of the type. It seems to be set up correctly, but
           * throws an error stating that getActions does not exist on the type.
           * @see https://deploy-preview-2385--material-ui-x.netlify.app/components/data-grid/columns/#column-menu
           * */
          columns={columns as GridColDef[]}
          sx={tableStyleOverrides}
          getRowId={(row) => row.integrationGUID + new Date().getMilliseconds()}
          hideFooter={isRunningInJest() ? true : false}
          pagination
          autoPageSize
        />
      </div>
      {toDelete && (
        <ConfirmModal
          onConfirm={onDeleteLicense}
          onDeny={() => setToDelete(null)}
          confirmContent="Are you sure you want to deactivate this license?"
          confirmTitle="Deactivate License"
          modalType="deactivate"
        />
      )}
    </Section>
  );
}

export default CertsAndLicensesList;
